import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Transition } from "react-transition-group";
import { Animation } from "../../../../../components/Animation";
import Input from "../../../../../components/Form/Input";
import Button from "../../../../../components/Button";
import Icon from "../../../../../components/Icon";
import DragDropArea from "../DragDropArea";
import linkIcon from "../../../../../assets/icons/chain-bold.svg";
import videoIcon from "../../../../../assets/icons/video_recorder_medium.svg";
import uploadIcon from "../../../../../assets/icons/cloud_upload.svg";


import {
  ModalBackdrop,
  Modal,
  ModalDialog,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Title,
  Close,
  ModalAnimation,
  Tabs,
  TabButton,
  ErrorText,
} from "./styles";

import closeIcon from "../../../../../assets/icons/close_circled.svg";

interface Props {
  onInsertUrl: (url: string) => void;
  onUploadLocal: (file: File) => void;
  onClose: () => void;
}

const isValidVideoUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return (
      /^(www\.)?youtube\.com$/.test(parsed.hostname) ||
      /^(www\.)?youtu\.be$/.test(parsed.hostname) ||
      /^(www\.)?vimeo\.com$/.test(parsed.hostname)
    );
  } catch {
    return false;
  }
};

const Component = ({ onInsertUrl, onUploadLocal, onClose }: Props) => {
  const [mode, setMode] = useState<"url" | "file">("url");
  const [inputUrl, setInputUrl] = useState("");
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Esc para cerrar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Gestión de estilos del body/HTML cuando el modal está abierto
  useEffect(() => {
    // Aplicar estilos cuando el modal se monta
    document.body.style.height = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.height = "100%";
    document.documentElement.style.overflow = "hidden";

    // Limpiar estilos cuando el modal se desmonta
    return () => {
      document.body.style.height = "auto";
      document.body.style.overflow = "auto";
      document.documentElement.style.height = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, []);

  const handleBackdropClick = () => {
    onClose();
  };

  const handleInsert = () => {
    if (mode === "url") {
      if (!inputUrl.trim()) {
        setError("Debes ingresar una URL");
        return;
      }

      if (!isValidVideoUrl(inputUrl.trim())) {
        setError("La URL debe ser de YouTube o Vimeo");
        return;
      }

      setError("");
      onInsertUrl(inputUrl.trim());
    } else {
      if (!selectedFile) {
        setError("Debes seleccionar un archivo de video");
        return;
      }

      setError("");
      onUploadLocal(selectedFile);
      onClose();
    }
  };

  const modalDOM = document.getElementById("modal") as HTMLElement;

  if (!modalDOM) {
    return null;
  }

  return ReactDOM.createPortal(
    <Transition in={true} timeout={350} mountOnEnter>
      {(state) => (
        <>
          <ModalAnimation
            name="fadeIn"
            className={`animation-${state}`}
          >
            <Modal>
              <ModalDialog width={520}>
                <ModalContent>
                  <ModalHeader>
                    <Close onClick={onClose}>
                      <Icon
                        icon={closeIcon}
                        size="24px"
                        color="black"
                      />
                    </Close>
                    <Title>Insertar video</Title>
                  </ModalHeader>

                  <ModalBody>
                    <Tabs>
                      <TabButton type="button" className={mode === "url" ? "active" : ""} onClick={() => {
                        setMode("url");
                        setError("");
                        setSelectedFile(null);
                      }}>
                        <Icon
                          icon={linkIcon}
                          size="18px"
                          color="black"
                        /> Desde la web
                      </TabButton>
                      <TabButton type="button" className={mode === "file" ? "active" : ""} onClick={() => {
                        setMode("file");
                        setError("");
                        setInputUrl("");
                      }}>
                        <Icon
                          icon={videoIcon}
                          size="18px"
                          color="black"
                        /> Desde el ordenador
                      </TabButton>
                    </Tabs>

                    {mode === "url" && (
                      <>
                        <Input
                          name="videoUrl"
                          errors={!!error ? { videoUrl: { message: error } } : {}}
                          value={inputUrl}
                          onChange={(e) => setInputUrl(e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          options={{
                            label: "URL del video",
                            size: "md",
                            skin: "base",
                            marginBottom: 12,
                            explainText: "Ingresá un enlace de YouTube o Vimeo en un formato válido.",
                          }}
                        />

                      </>
                    )}

                    {mode === "file" && (
                      <DragDropArea
                        accept="video/mp4,video/webm,video/quicktime"
                        onFileSelect={(file) => {
                          setSelectedFile(file);
                          setError("");
                        }}
                        onError={(errorMsg) => setError(errorMsg)}
                        placeholder="O arrastrá un archivo de video aquí"
                        helperText={<>Se aceptan archivos .mp4, .mov, .webm <br />de hasta 2GB</>}
                        maxSize={2048} // 2GB in MB
                        icon={uploadIcon}
                        buttonText="Seleccionar video"
                      />
                    )}
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      type="button"
                      onClick={onClose}
                      options={{
                        type: "outline",
                        size: "md",
                        skin: "purple",
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      onClick={handleInsert}
                      options={{
                        type: "filled",
                        size: "md",
                        skin: "purple",
                      }}
                      disabled={!inputUrl && !selectedFile}
                    >
                      Insertar
                    </Button>

                  </ModalFooter>
                </ModalContent>
              </ModalDialog>
            </Modal>
          </ModalAnimation>

          <Animation name="fadeIn" className={`animation-${state}`}>
            <ModalBackdrop onClick={handleBackdropClick} />
          </Animation>
        </>
      )}
    </Transition>,
    modalDOM
  );
};

export default Component;
